import {useState, useRef, useEffect} from 'react'
import './index.scss'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import classNames from 'classnames'
import {useSelector, useDispatch} from 'react-redux'
import {Formik} from 'formik'
import {mfaCodeSchema} from '../../utils/validation'
import Footer from '../../components/layouts/footer'
import {PageSubTitle, PageTitle, ResponsivePageTitle} from '../../components/core/title'
import {
    getMFAStatus,
    enableMFA,
    verifyMFA,
    disableMFA,
    regenerateRecoveryCodes,
    resetState,
    MFA_STEPS
} from '../../stores/security'
import DisabledView from '../../components/pages/security/DisabledView.jsx'
import LoadingView from '../../components/pages/security/LoadingView.jsx'
import SetupView from '../../components/pages/security/SetupView'
import EnabledView from '../../components/pages/security/EnabledView'
import RecoveryCodesView from '../../components/pages/security/RecoveryCodesView'
import AccountSection from '../../components/pages/security/AccountSection'
import PasswordSection from '../../components/pages/security/PasswordSection'
import RecoveryCodesSection from '../../components/pages/security/RecoveryCodesSection'
import DeleteAccountSection from '../../components/pages/security/DeleteAccountSection'

const OTP_LENGTH = 6
const EMPTY_OTP = Array(OTP_LENGTH).fill('')

const Security = () => {
    const text = useNamespaceTranslations('security')
    const dispatch = useDispatch()
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const {status: mfaStatus, step, qrData, recoveryCodes} = useSelector(state => state.security)

    const [otpCode, setOtpCode] = useState(EMPTY_OTP)
    const inputRefs = useRef([])

    const handleCancel = () => {
        dispatch(resetState())
        setOtpCode(EMPTY_OTP)
    }

    const handleVerify = async (values, {setFieldError}) => {
        await dispatch(verifyMFA(values.otp_code, setFieldError))
        setOtpCode(EMPTY_OTP)
    }

    const handleOtpChange = (index, value, setFieldValue) => {
        if (value.length > 1 || (value && !/^\d$/.test(value))) return
        const newCode = [...otpCode]
        newCode[index] = value
        setOtpCode(newCode)
        setFieldValue('otp_code', newCode.join(''))
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus()
        }
    }

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus()
        }
    }

    const handleOtpPaste = (e, setFieldValue) => {
        e.preventDefault()
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
        if (!pasted.length) return
        const newCode = [...otpCode]
        for (let i = 0; i < pasted.length; i++) newCode[i] = pasted[i]
        setOtpCode(newCode)
        setFieldValue('otp_code', newCode.join(''))
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
    }

    const renderContent = () => {
        if (step === MFA_STEPS.LOADING) {
            return <LoadingView text={text} />
        }

        if (!mfaStatus.enabled && (step === MFA_STEPS.RECOVERY_CODES || step === MFA_STEPS.REGENERATING_CODES)) {
            return (
                <RecoveryCodesView
                    text={text}
                    recoveryCodes={recoveryCodes}
                    step={step}
                    onDone={() => dispatch(resetState())}
                    onRegenerate={() => dispatch(regenerateRecoveryCodes())}
                />
            )
        }

        if (step === MFA_STEPS.QR || step === MFA_STEPS.ENABLING) {
            return (
                <Formik
                    initialValues={{otp_code: ''}}
                    validationSchema={mfaCodeSchema}
                    onSubmit={handleVerify}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    {({errors, handleSubmit, setFieldValue}) => (
                        <SetupView
                            text={text}
                            qrData={qrData}
                            otpCode={otpCode}
                            error={errors.otp_code}
                            step={step}
                            onVerify={handleSubmit}
                            onCancel={handleCancel}
                            onChange={(index, value) => handleOtpChange(index, value, setFieldValue)}
                            onKeyDown={handleOtpKeyDown}
                            onPaste={(e) => handleOtpPaste(e, setFieldValue)}
                            inputRefs={inputRefs}
                        />
                    )}
                </Formik>
            )
        }

        if (mfaStatus.enabled) {
            const showRecoveryCodes = step === MFA_STEPS.RECOVERY_CODES || step === MFA_STEPS.REGENERATING_CODES
            return (
                <>
                    <EnabledView
                        text={text}
                        step={step}
                        onDisable={() => dispatch(disableMFA())}
                    />
                    {showRecoveryCodes && (
                        <RecoveryCodesView
                            text={text}
                            recoveryCodes={recoveryCodes}
                            step={step}
                            onDone={() => dispatch(resetState())}
                            onRegenerate={() => dispatch(regenerateRecoveryCodes())}
                        />
                    )}
                </>
            )
        }

        return (
            <DisabledView
                text={text}
                onEnable={() => dispatch(enableMFA())}
            />
        )
    }

    useEffect(() => {
        dispatch(getMFAStatus())
    }, [])

    return (
        <main className={classNames('main security', {
            dark: isDarkMode
        })}>
            <div>
                <ResponsivePageTitle/>
                <div className='content-title'>
                    <div className='left-side'>
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.breadcrumb}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1 className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <div className='content-body'>
                    <AccountSection text={text}/>
                    <PasswordSection text={text}/>
                    {renderContent()}
                    {step !== MFA_STEPS.RECOVERY_CODES && step !== MFA_STEPS.REGENERATING_CODES && (
                        <RecoveryCodesSection
                            text={text}
                            disabled={!mfaStatus.enabled}
                            onRegenerate={() => dispatch(regenerateRecoveryCodes())}
                        />
                    )}
                    <DeleteAccountSection text={text}/>
                </div>
            </div>
            <Footer/>
        </main>
    )
}

export default Security
